import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Coins, Users, ChevronRight, Hexagon, Info, Flame, Share2, Trophy, Download, Chrome, Swords, Gift, ArrowRight, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExtensionHome({ onNavigate }) {
  const { user, token, API } = useAuth();
  const [stats, setStats] = useState(null);
  const [showCreators, setShowCreators] = useState(false);
  const [creators, setCreators] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch (e) { console.error(e); }
  }, [API, token]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const fetchCreators = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/links/recent?limit=100`, { headers: { Authorization: `Bearer ${token}` } });
      const links = res.data;
      const creatorMap = {};
      links.forEach(l => {
        if (!creatorMap[l.creator_name]) {
          creatorMap[l.creator_name] = { name: l.creator_name, network: l.affiliate_network, count: 0, domain: l.domain };
        }
        creatorMap[l.creator_name].count++;
      });
      setCreators(Object.values(creatorMap).sort((a, b) => b.count - a.count));
    } catch (e) { console.error(e); }
  }, [API, token]);

  const handleShowCreators = () => {
    setShowCreators(true);
    fetchCreators();
  };

  const level = user?.level || 1;
  const points = user?.points || 0;
  const pointsInLevel = points % 50;
  const progressPercent = (pointsInLevel / 50) * 100;
  const hasExtension = (user?.links_protected || 0) > 0;

  return (
    <div className="p-5 space-y-4">
      {/* Status Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="hex-clip w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
            <span className="text-black font-heading font-black text-sm">{level}</span>
          </div>
          <div>
            <p className="text-sm font-bold text-white">{user?.name || 'User'}</p>
            <p className="text-xs text-zinc-500">Level {level} Protector</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(stats?.daily_streak || user?.daily_streak || 0) > 0 && (
            <div className="flex items-center gap-1 px-2 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20" data-testid="streak-badge">
              <Flame className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-xs font-bold font-mono text-orange-400">{stats?.daily_streak || user?.daily_streak || 0}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20">
            <Coins className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-sm font-bold font-mono text-amber-500">{points}</span>
          </div>
        </div>
      </div>

      {/* Conditional: Extension installed vs. not */}
      {hasExtension ? (
        <>
          {/* Protection Active */}
          <div className="glass-card rounded-xl p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-emerald-400">Protection Active</p>
              <p className="text-[10px] text-zinc-500">Affiliate links are being monitored</p>
            </div>
          </div>

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="stat-links-protected"
              onClick={() => onNavigate('dashboard')}
              className="glass-card rounded-xl p-4 text-left hover:border-amber-500/20 transition-all"
            >
              <ShieldCheck className="w-5 h-5 text-amber-500 mb-2" />
              <p className="font-heading font-black text-2xl text-white">{stats?.total_links_protected || 0}</p>
              <p className="text-xs text-zinc-500 mt-0.5">Links Protected</p>
            </button>
            <button
              data-testid="stat-creators"
              onClick={handleShowCreators}
              className="glass-card rounded-xl p-4 text-left hover:border-blue-500/20 transition-all"
            >
              <Users className="w-5 h-5 text-blue-400 mb-2" />
              <p className="font-heading font-black text-2xl text-white">{stats?.creators_supported || 0}</p>
              <p className="text-xs text-zinc-500 mt-0.5">Creators <ChevronRight className="w-3 h-3 inline" /></p>
            </button>
          </div>

          {/* Creator List Overlay */}
          <AnimatePresence>
            {showCreators && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="glass-card rounded-xl p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold text-zinc-400">Creators You Protect</p>
                  <button onClick={() => setShowCreators(false)} className="p-1 rounded-md hover:bg-white/5">
                    <X className="w-3.5 h-3.5 text-zinc-500" />
                  </button>
                </div>
                {creators.length === 0 ? (
                  <p className="text-xs text-zinc-600 text-center py-4">No creators protected yet</p>
                ) : (
                  <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                    {creators.map((c, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.04]" data-testid={`creator-item-${i}`}>
                        <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                          <Users className="w-3.5 h-3.5 text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-white truncate">{c.name}</p>
                          <p className="text-[9px] text-zinc-600">{c.network} &middot; {c.domain}</p>
                        </div>
                        <span className="text-[10px] font-mono text-amber-500">{c.count}x</span>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </>
      ) : (
        <>
          {/* Install Extension CTA */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl overflow-hidden border border-amber-500/20"
          >
            <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                  <Chrome className="w-6 h-6 text-amber-500" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-white mb-1">Install the Extension</h3>
                  <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
                    Get the Honey Pot Chrome extension to start protecting creator affiliate links as you browse. Earn points for every link you protect.
                  </p>
                  <a
                    href={`${API_URL}/api/extension/download`}
                    download
                    className="honey-btn inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold"
                    data-testid="home-install-btn"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download Extension
                  </a>
                </div>
              </div>
            </div>
            <div className="bg-white/[0.02] px-5 py-3 border-t border-white/5">
              <div className="flex items-center gap-4 text-[10px] text-zinc-500">
                <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-emerald-400" /> Blocks hijackers</span>
                <span className="flex items-center gap-1"><Coins className="w-3 h-3 text-amber-400" /> Earn points</span>
                <span className="flex items-center gap-1"><Users className="w-3 h-3 text-blue-400" /> Support creators</span>
              </div>
            </div>
          </motion.div>

          {/* Get Started Cards */}
          <div>
            <p className="text-xs font-bold text-zinc-400 mb-2">Get started while you wait</p>
            <div className="space-y-2">
              <button
                onClick={() => onNavigate('quests')}
                className="w-full glass-card rounded-xl p-3.5 flex items-center gap-3 hover:border-amber-500/20 transition-all text-left"
                data-testid="home-quests-cta"
              >
                <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <Swords className="w-4.5 h-4.5 text-red-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-white">Complete Quests</p>
                  <p className="text-[10px] text-zinc-500">Earn bonus points right now</p>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-600" />
              </button>
              <button
                onClick={() => onNavigate('rewards')}
                className="w-full glass-card rounded-xl p-3.5 flex items-center gap-3 hover:border-amber-500/20 transition-all text-left"
                data-testid="home-rewards-cta"
              >
                <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                  <Gift className="w-4.5 h-4.5 text-purple-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-white">Browse Rewards</p>
                  <p className="text-[10px] text-zinc-500">See what you can earn</p>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-600" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Level Progress */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-zinc-500">Level {level} Progress</span>
          <span className="text-xs font-mono text-amber-500">{pointsInLevel}/50</span>
        </div>
        <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
        </div>
        <p className="text-[10px] text-zinc-600 mt-1.5">{50 - pointsInLevel} points to Level {level + 1}</p>
      </div>

      {/* Email Verification Banner */}
      {user && !user.email_verified && (
        <div className="rounded-xl p-3 border border-amber-500/20 bg-amber-500/5 flex items-center gap-2.5">
          <Info className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-amber-400">Verify your email</p>
            <p className="text-[10px] text-zinc-500">Complete verification to unlock all features</p>
          </div>
          <a href="/verify-email" className="text-[10px] text-amber-500 font-medium hover:text-amber-400" data-testid="verify-email-link">Verify</a>
        </div>
      )}

      {/* Share + Leaderboard Row */}
      <div className="grid grid-cols-2 gap-3">
        <button
          data-testid="share-impact-btn"
          onClick={() => onNavigate('share')}
          className="glass-card rounded-xl py-3 text-center hover:border-purple-500/20 transition-all flex flex-col items-center gap-1.5"
        >
          <Share2 className="w-4 h-4 text-purple-400" />
          <span className="text-[10px] text-purple-400 font-medium">Share Impact</span>
        </button>
        <button
          data-testid="leaderboard-btn"
          onClick={() => onNavigate('leaderboard')}
          className="glass-card rounded-xl py-3 text-center hover:border-amber-500/20 transition-all flex flex-col items-center gap-1.5"
        >
          <Trophy className="w-4 h-4 text-amber-400" />
          <span className="text-[10px] text-amber-400 font-medium">Rankings</span>
        </button>
      </div>

      {/* Recent Activity Preview - Only show if they have links */}
      {stats?.recent_links?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-zinc-400">Recent Activity</p>
            <button
              data-testid="view-all-activity"
              onClick={() => onNavigate('dashboard')}
              className="text-[10px] text-amber-500 flex items-center gap-0.5 hover:text-amber-400"
            >
              View all <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-2">
            {stats.recent_links.slice(0, 2).map((link, i) => (
              <div key={link.id || i} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                <div className="w-6 h-6 rounded-md bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                  <Hexagon className="w-3 h-3 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white truncate">{link.creator_name}</p>
                  <p className="text-[10px] text-zinc-600 truncate">{link.domain}</p>
                </div>
                <span className="text-[10px] font-mono text-amber-500/70">+{link.points_earned}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
